import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { badgeFairnessApi } from '../../services/api';

// Per-retailer net-giver-score trend off the daily retailer_click_balance snapshots
// (spec v1.3.2: history is free — this chart is why the table is append-only).
// NGS 1.0 = perfectly balanced; above = net giver (promoted when out of band),
// below = net receiver (demoted when out of band).

const RETAILER_COLORS = [
  '#1976d2',
  '#2e7d32',
  '#ed6c02',
  '#9c27b0',
  '#d32f2f',
  '#0288d1',
  '#7b1fa2',
  '#5d4037',
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      {payload
        .filter((p) => p.value != null)
        .sort((a, b) => b.value - a.value)
        .map((p) => (
          <Typography key={p.dataKey} variant="caption" sx={{ display: 'block', color: p.color }}>
            {p.dataKey}: {Number(p.value).toFixed(3)}
          </Typography>
        ))}
    </Paper>
  );
};

const BadgeFairnessTrends = () => {
  const [days, setDays] = useState(90);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrends = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await badgeFairnessApi.getTrends({ days });
      if (result.success) {
        setRows(result.data.rows);
      } else {
        setError(result.error || 'Failed to load NGS trends');
      }
    } catch (e) {
      setError(e.message || 'Failed to load NGS trends');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  const retailers = [...new Set((rows || []).map((r) => r.retailer_id))];
  const byDate = {};
  (rows || []).forEach((r) => {
    byDate[r.snapshot_date] = byDate[r.snapshot_date] || { snapshot_date: r.snapshot_date };
    byDate[r.snapshot_date][r.retailer_id] = r.net_giver_score;
  });
  const chartData = Object.values(byDate).sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date));

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Net-giver score trend (daily snapshots)
        </Typography>
        <ToggleButtonGroup size="small" value={days} exclusive onChange={(_, v) => v && setDays(v)}>
          <ToggleButton value={30}>30d</ToggleButton>
          <ToggleButton value={90}>90d</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && (
        <Box sx={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="snapshot_date" tick={{ fontSize: 11 }} minTickGap={32} />
              <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={1} stroke="#888" strokeDasharray="4 4" label={{ value: 'balanced', fontSize: 11 }} />
              {retailers.map((rid, i) => (
                <Line
                  key={rid}
                  type="monotone"
                  dataKey={rid}
                  stroke={RETAILER_COLORS[i % RETAILER_COLORS.length]}
                  dot={false}
                  strokeWidth={1.5}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default BadgeFairnessTrends;
